import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ops from "../../styles/opsConsole.module.css";
import styles from "./MarginAgreement.module.css";

/**
 * MARGIN AGREEMENT
 * ================
 * The commercial term panel for one vendor: the carve-out margin that IS
 * Easexpense's revenue line, and the only place it can be set.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * `vendors.margin_rate` / `vendors.margin_flat_cut` have existed in the schema
 * for a while and NOTHING wrote them — every vendor sits at NULL, meaning no
 * agreement is on record and the platform default silently carries them all.
 * A marketplace whose take rate is an environment variable is a marketplace
 * that cannot negotiate.
 *
 * WHAT THE RATE MEANS (the offer model — utils/offerPricing.js)
 * --------------------------------------------------------------
 * The rate is the DISCOUNT OFF THE INDUSTRY (LIST) PRICE the vendor owes us:
 *
 *     list price      ₹204           what the product lists at
 *     at 20%          ≤ ₹163.20      the most the vendor may charge us
 *     buyer pays      set per price at approval (their price + what we keep)
 *
 * It is a CEILING on the vendor's price, checked when they submit and again at
 * approval. It never moves a price or a payout: what they charge us is what
 * they type. The panel used to model the old carve-out (a share of the BUYER
 * price, payout = buyer × (1 − rate)), which showed Ingram's ₹163.20 cost
 * "becoming" ₹146.88 — arithmetic nothing does any more.
 *
 * The panel refuses to let anyone set a term blind: every keystroke re-asks the
 * server what this rate would do to that vendor's actual live tiers, and the
 * save button stays out of reach until that answer is on screen.
 */

const API = process.env.REACT_APP_SERVER_URL;

const get = (path) =>
  fetch(`${API}${path}`, {
    headers: { Accept: "application/json" },
    credentials: "include",
  }).then((r) => r.json());

const post = (path, body) =>
  fetch(`${API}${path}`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  }).then((r) => r.json());

/* ── formatting ───────────────────────────────────────────────────────── */

// Under ₹1 a two-decimal figure rounds to ₹0.00 and hides the cut, so those
// show four decimals. The maximum must never sit below the minimum:
// toLocaleString throws a RangeError on that, and an unhandled throw in render
// took the whole admin page down the first time a tier's per-unit cut fell
// between ₹0 and ₹1 (min 4, max 2).
const inr = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const digits = Math.abs(v) < 1 && v !== 0 ? 4 : 0;
  return `₹${v.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: Math.max(digits, 2),
  })}`;
};

// A rate is stored as a fraction and shown as a percentage. Two decimals only
// when they carry information — "20%" reads better than "20.00%", but a
// negotiated 12.5% must not round to 13%.
const pct = (fraction) => {
  if (fraction === null || fraction === undefined) return "—";
  const p = Number(fraction) * 100;
  return `${Number(p.toFixed(2))}%`;
};

const when = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

/** One agreement rendered as a phrase, for the history log. */
const describe = (a) => {
  if (!a || !a.kind) return "—";
  if (a.kind === "flat") return `${inr(a.value)} off list per unit`;
  return `${pct(a.value)} off list${a.source === "platform" ? " (platform default)" : ""}`;
};

/* ── modes ────────────────────────────────────────────────────────────── */
/* The three states the agreement can be in. Exclusive by construction: the
   selected mode decides which single column gets written, so "both set" — the
   condition utils/margin.js can only warn about at read time — is unreachable
   from this UI. */
/** Live tiers against the ceiling: list, the most they may charge, what they
 *  charge, the buyer price and what Easexpense keeps. */
function TierTable({ tiers }) {
  const label = { within: "within", over: "over the ceiling", no_list: "no list price" };
  const tone = { within: ops.ok, over: ops.warn, no_list: ops.muted };
  return (
    <div style={{ overflowX: "auto" }}>
      <table className={ops.table}>
        <thead>
          <tr>
            <th>Plan · seats</th>
            <th className={ops.num}>List price</th>
            <th className={ops.num}>Most they may charge</th>
            <th className={ops.num}>Their price to us</th>
            <th className={ops.num}>Discount given</th>
            <th className={ops.num}>Buyer pays</th>
            <th className={ops.num}>Easexpense keeps</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tiers.map((t) => (
            <tr key={t.unit_id}>
              <td>
                {t.plan}
                <div className={ops.mono} style={{ fontSize: 11.5, color: "#7b8798" }}>
                  {t.range} seats · tier #{t.unit_id}
                </div>
              </td>
              <td className={ops.num}>{t.industry_price ? inr(t.industry_price) : "—"}</td>
              <td className={ops.num}>
                {t.max_price_to_us === null || t.max_price_to_us === undefined ? "—" : inr(t.max_price_to_us)}
              </td>
              <td className={ops.num}>
                <b>{inr(t.vendor_price)}</b>
              </td>
              <td className={ops.num}>
                {t.discount_given === null || t.discount_given === undefined ? "—" : pct(t.discount_given)}
              </td>
              <td className={ops.num}>{inr(t.marketplace_price)}</td>
              <td className={ops.num}>
                {inr(t.easexpense_keeps)}
                {t.keeps_rate !== null && t.keeps_rate !== undefined ? ` (${pct(t.keeps_rate)})` : ""}
              </td>
              <td>
                <span className={`${ops.badge} ${tone[t.status] || ops.muted}`}>
                  {label[t.status] || t.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const MODES = [
  {
    key: "rate",
    title: "Percentage",
    sub: "At least this % off the list price",
  },
  {
    key: "flat",
    title: "Flat amount",
    sub: "At least this much off the list price, per unit",
  },
  {
    key: "default",
    title: "Platform default",
    sub: "No negotiated term — falls back to the platform rate",
  },
];

/* ══════════════════════════════════════════════════════════════════════ */

/**
 * @param {number}   vendorId  the vendor whose agreement this is
 * @param {function} [onChange] optional — called after a successful save, so a
 *   parent page can refetch. The margin shows up in the vendor list KPIs and in
 *   its own "needs attention" checks, and those go stale the moment this saves.
 */
export default function MarginAgreement({ vendorId, onChange, embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Draft state — what the admin is proposing, not what is in force.
  const [mode, setMode] = useState("default");
  const [rateInput, setRateInput] = useState(""); // as a PERCENT, e.g. "22"
  const [flatInput, setFlatInput] = useState(""); // rupees per unit
  const [note, setNote] = useState("");
  const [ack, setAck] = useState(false);

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState("");

  // Guards a stale in-flight preview from overwriting a newer one.
  const previewSeq = useRef(0);

  /* ── load ──────────────────────────────────────────────────────────── */

  const reset = useCallback((d) => {
    const c = (d && d.current) || {};
    if (c.margin_flat_cut !== null && c.margin_flat_cut !== undefined) {
      setMode("flat");
      setFlatInput(String(c.margin_flat_cut));
      setRateInput("");
    } else if (c.margin_rate !== null && c.margin_rate !== undefined) {
      setMode("rate");
      setRateInput(String(Number((Number(c.margin_rate) * 100).toFixed(4))));
      setFlatInput("");
    } else {
      setMode("default");
      setRateInput("");
      setFlatInput("");
    }
    setNote("");
    setAck(false);
    setPreview(null);
    setPreviewError("");
    setSaveError("");
  }, []);

  const load = useCallback(() => {
    if (vendorId === null || vendorId === undefined || vendorId === "") return;
    setLoading(true);
    get(`/admin/vendors/${vendorId}/margin`)
      .then((r) => {
        if (!r || r.success === false) {
          setLoadError(r?.msg || "Could not load the margin agreement.");
          setData(null);
          return;
        }
        setLoadError("");
        setData(r);
        reset(r);
      })
      .catch(() => setLoadError("Could not reach the server."))
      .finally(() => setLoading(false));
  }, [vendorId, reset]);

  useEffect(load, [load]);

  /* ── the proposal, normalised the way the API wants it ─────────────── */

  const proposal = useMemo(() => {
    if (mode === "default") return {};
    if (mode === "rate") {
      const p = Number(rateInput);
      if (rateInput === "" || !Number.isFinite(p)) return null;
      // Percent in the box, fraction on the wire. 4dp matches DECIMAL(5,4).
      return { margin_rate: Number((p / 100).toFixed(4)) };
    }
    const f = Number(flatInput);
    if (flatInput === "" || !Number.isFinite(f)) return null;
    return { margin_flat_cut: Number(f.toFixed(2)) };
  }, [mode, rateInput, flatInput]);

  // Has the admin actually proposed something different from what is in force?
  const changed = useMemo(() => {
    if (!data || !proposal) return false;
    const c = data.current || {};
    const curRate = c.margin_rate === null || c.margin_rate === undefined ? null : Number(c.margin_rate);
    const curFlat =
      c.margin_flat_cut === null || c.margin_flat_cut === undefined ? null : Number(c.margin_flat_cut);
    const nextRate = proposal.margin_rate === undefined ? null : proposal.margin_rate;
    const nextFlat = proposal.margin_flat_cut === undefined ? null : proposal.margin_flat_cut;
    return curRate !== nextRate || curFlat !== nextFlat;
  }, [data, proposal]);

  /* ── consequence preview ───────────────────────────────────────────── */
  /* Debounced, because it queries every live tier the vendor has. The admin
     types "2", "22" — only the pause is worth a round trip. */

  useEffect(() => {
    if (!data || data.is_house_vendor) return;
    if (!changed || !proposal) {
      setPreview(null);
      setPreviewError("");
      return;
    }
    const seq = ++previewSeq.current;
    const qs =
      proposal.margin_rate !== undefined
        ? `?margin_rate=${proposal.margin_rate}`
        : proposal.margin_flat_cut !== undefined
          ? `?margin_flat_cut=${proposal.margin_flat_cut}`
          : "?margin_rate=";
    setPreviewing(true);
    const timer = setTimeout(() => {
      get(`/admin/vendors/${vendorId}/margin${qs}`)
        .then((r) => {
          if (seq !== previewSeq.current) return; // a newer keystroke won
          if (!r || r.success === false) {
            setPreview(null);
            setPreviewError(r?.msg || "Could not model that rate.");
            return;
          }
          setPreviewError("");
          setPreview(r);
        })
        .catch(() => {
          if (seq === previewSeq.current) setPreviewError("Could not reach the server.");
        })
        .finally(() => {
          if (seq === previewSeq.current) setPreviewing(false);
        });
    }, 350);
    return () => clearTimeout(timer);
  }, [vendorId, data, changed, proposal]);

  /* ── save ──────────────────────────────────────────────────────────── */

  const save = () => {
    if (!proposal) return;
    setSaving(true);
    setSaveError("");
    setSaved("");
    post(`/admin/vendors/${vendorId}/margin`, {
      ...proposal,
      note: note.trim() || null,
      ...(ack ? { acknowledge_zero_payout: true } : {}),
    })
      .then((r) => {
        if (!r || r.success === false) {
          setSaveError(r?.msg || "Could not save the agreement.");
          // The server asks for an explicit acknowledgement when a flat cut
          // would pay ₹0 on a live tier. Surface the checkbox rather than
          // leaving the admin staring at a refusal they cannot act on.
          if (r?.requires_acknowledgement === "acknowledge_zero_payout") setAck(false);
          return;
        }
        setSaved(r.msg || "Agreement updated.");
        load();
        if (typeof onChange === "function") onChange();
      })
      .catch(() => setSaveError("Could not reach the server."))
      .finally(() => setSaving(false));
  };

  /* ── render ────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <section className={`${ops.card} ${styles.wrap}`}>
        <h3>Commercial agreement</h3>
        <p className={ops.empty}>Loading…</p>
      </section>
    );
  }

  if (loadError || !data) {
    return (
      <section className={`${ops.card} ${styles.wrap}`}>
        <h3>Commercial agreement</h3>
        <p className={ops.empty}>{loadError || "No agreement data."}</p>
      </section>
    );
  }

  const current = data.current || {};
  const impact = data.current_impact || {};
  const negotiated = !!current.is_negotiated;

  // The house vendor buys at real per-product costs and authors its own
  // vendor_price; no carve-out is derived for it. Showing a control that
  // writes a term nothing reads would be worse than showing nothing.
  if (data.is_house_vendor) {
    return (
      <section className={`${ops.card} ${styles.wrap}`}>
        <h3>Commercial agreement</h3>
        <div className={`${ops.cardPad} ${ops.noteBox}`}>
          <b>Easexpense sells this catalogue as itself.</b>
          <p>
            The house vendor buys each product at a negotiated supplier cost — Google
            Workspace, Microsoft 365 and Threatcop are all different — so its
            vendor price is a real external cost, not a number derived from a
            platform rate. A carve-out margin does not apply here.
          </p>
        </div>
      </section>
    );
  }

  // The illustration uses one of this vendor's real tiers that has a list
  // price, so the arithmetic on screen is theirs and not a stock ₹100.
  const example =
    (impact.sample || []).find((t) => t.industry_price > 0 && t.status === "within") ||
    (impact.sample || []).find((t) => t.industry_price > 0) ||
    null;
  const exList = example ? example.industry_price : 100;
  const exCeiling =
    current.kind === "flat"
      ? Math.max(0, exList - Number(current.value))
      : exList * (1 - Number(current.value));

  const shown = preview && preview.proposed_impact ? preview.proposed_impact : null;
  const proposedAgreement = preview ? preview.proposed : null;
  // A flat amount at or above a tier's list price leaves them nothing to charge.
  const zeroPayoutRisk = shown && shown.tiers_with_zero_ceiling > 0 && mode === "flat";

  return (
    <section className={`${ops.card} ${styles.wrap}`}>
      {/* Inside a vendor-record section the section header already names
          this and carries the status, so it is not said twice. */}
      {!embedded && (
        <h3>
          Commercial agreement
          <span className={`${ops.badge} ${negotiated ? ops.ok : ops.muted}`}>
            <i className={ops.dot} />
            {negotiated ? "Negotiated" : "Platform default"}
          </span>
        </h3>
      )}

      {/* ── what is in force right now ──────────────────────────────── */}
      <div className={ops.cardPad}>
        <div className={styles.headline}>
          <span className={styles.rate}>
            {current.kind === "flat" ? inr(current.value) : pct(current.value)}
            <span className={styles.rateUnit}>
              {current.kind === "flat" ? " off list, per unit" : " off the list price"}
            </span>
          </span>
        </div>
        <p className={styles.headlineNote}>
          {negotiated ? (
            <>
              Agreed with this vendor: their price to us must be at least{" "}
              {current.kind === "flat" ? `${inr(current.value)} per unit` : pct(current.value)}{" "}
              below the industry (list) price. It is checked when they submit
              and at approval. What the buyer pays — and so what Easexpense
              keeps — is set per price at approval.
            </>
          ) : (
            <>
              <b>Nothing has been agreed with this vendor.</b> The platform
              default applies: their price to us must be at least{" "}
              {pct(data.platform_default_rate)} below the list price. It is a
              fallback, not a negotiated term, and it moves if the platform
              default moves.
            </>
          )}
        </p>

        {/* The rule drawn on one of their real tiers: list price, the most
            they may charge us at this rate, and what actually happened. */}
        <div className={styles.split}>
          <div className={styles.splitCell}>
            <span className={styles.splitLbl}>List price</span>
            <span className={styles.splitVal}>{inr(exList)}</span>
            <span className={styles.splitSub}>{example ? example.plan : "example"}</span>
          </div>
          <div className={styles.splitCell}>
            <span className={styles.splitLbl}>Most they may charge us</span>
            <span className={styles.splitVal}>{inr(exCeiling)}</span>
            <span className={styles.splitSub}>
              {example ? `they charge ${inr(example.vendor_price)}` : "list minus the agreed discount"}
            </span>
          </div>
          <div className={styles.splitCell}>
            <span className={styles.splitLbl}>Buyer pays</span>
            <span className={styles.splitVal}>{example ? inr(example.marketplace_price) : "—"}</span>
            <span className={styles.splitSub}>
              {example
                ? `set at approval · Easexpense keeps ${inr(example.easexpense_keeps)}`
                : "set per price at approval"}
            </span>
          </div>
        </div>
      </div>

      {data.conflict && (
        <div className={`${ops.cardPad} ${ops.attention}`}>
          <b>Both a rate and a flat cut are stored on this vendor.</b>
          <ul>
            <li>
              The flat cut is winning ({inr(current.margin_flat_cut)}); the rate
              of {pct(current.margin_rate)} is being ignored. Saving from this
              panel clears whichever one you do not choose.
            </li>
          </ul>
        </div>
      )}

      {/* ── where live rows actually sit ────────────────────────────── */}
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statVal}>{impact.live_tiers ?? 0}</span>
          <span className={styles.statLbl}>live price tiers</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statVal}>{impact.tiers_within ?? 0}</span>
          <span className={styles.statLbl}>give at least the agreed discount</span>
        </div>
        <div className={styles.stat}>
          <span
            className={styles.statVal}
            style={{ color: impact.tiers_over_ceiling ? "#a15c02" : undefined }}
          >
            {impact.tiers_over_ceiling ?? 0}
          </span>
          <span className={styles.statLbl}>
            charge us more than it allows
            {impact.tiers_without_list_price
              ? ` · ${impact.tiers_without_list_price} with no list price`
              : ""}
          </span>
        </div>
      </div>
      <p className={ops.note}>
        {impact.live_tiers
          ? "This screen never changes a price or a payout. The rate is the ceiling a vendor's price to us is checked against: a price over it is refused when they submit and flagged at approval."
          : "This vendor has no live price tiers yet. The agreement below is the ceiling their first prices will be checked against."}
      </p>
      {(impact.tiers_over_ceiling > 0 || impact.tiers_without_list_price > 0) && (
        <TierTable tiers={(impact.sample || []).filter((t) => t.status !== "within")} />
      )}

      {/* ── change it ───────────────────────────────────────────────── */}
      <div className={ops.cardPad} style={{ borderTop: "1px solid #e4e9f0" }}>
        <span className={styles.fieldLbl}>How is the discount set?</span>
        <div className={styles.modes}>
          {MODES.map((m) => (
            <label
              key={m.key}
              className={`${styles.mode} ${mode === m.key ? styles.modeOn : ""}`}
            >
              <input
                type="radio"
                name={`margin-mode-${vendorId}`}
                checked={mode === m.key}
                onChange={() => {
                  setMode(m.key);
                  setAck(false);
                  setSaved("");
                  setSaveError("");
                  // Switching modes must not wipe what was typed: going Percentage
                  // -> Flat -> Percentage used to come back to an empty box whose
                  // grey "20" placeholder looked like a value. Each box keeps its
                  // own input; only the SELECTED mode is sent (see `proposal`), so
                  // the backend's rate-or-flat exclusivity still holds. An empty
                  // box is refilled from the agreement currently in force.
                  const c = (data && data.current) || {};
                  if (m.key === "rate" && rateInput === "" && c.margin_rate !== null && c.margin_rate !== undefined) {
                    setRateInput(String(Number((Number(c.margin_rate) * 100).toFixed(4))));
                  }
                  if (m.key === "flat" && flatInput === "" && c.margin_flat_cut !== null && c.margin_flat_cut !== undefined) {
                    setFlatInput(String(c.margin_flat_cut));
                  }
                }}
              />
              <span className={styles.modeTitle}>{m.title}</span>
              <span className={styles.modeSub}>{m.sub}</span>
            </label>
          ))}
        </div>

        {mode === "rate" && (
          <div className={styles.field}>
            <span className={styles.fieldLbl}>Required discount off the list price</span>
            <div className={styles.inputRow}>
              <input
                className={styles.input}
                style={{ borderRadius: "9px 0 0 9px" }}
                type="number"
                min="0"
                max="90"
                step="0.1"
                value={rateInput}
                placeholder="e.g. 18"
                onChange={(e) => {
                  setRateInput(e.target.value);
                  setSaved("");
                }}
              />
              <span className={`${styles.affix} ${styles.affixPost}`}>%</span>
            </div>
            <p className={styles.hint}>
              Entered as a percentage, stored as a fraction:{" "}
              <span className={ops.mono}>
                {proposal && proposal.margin_rate !== undefined
                  ? proposal.margin_rate.toFixed(4)
                  : "—"}
              </span>
              . Anything above 90% is refused as a typo.
            </p>
          </div>
        )}

        {mode === "flat" && (
          <div className={styles.field}>
            <span className={styles.fieldLbl}>Required discount per unit</span>
            <div className={styles.inputRow}>
              <span className={`${styles.affix} ${styles.affixPre}`}>₹</span>
              <input
                className={styles.input}
                style={{ borderRadius: "0 9px 9px 0" }}
                type="number"
                min="0"
                step="0.01"
                value={flatInput}
                placeholder="e.g. 40"
                onChange={(e) => {
                  setFlatInput(e.target.value);
                  setSaved("");
                }}
              />
            </div>
            <p className={styles.hint}>
              The same amount off every tier's list price — a bigger share of a
              cheap tier than of an expensive one. Check the preview.
            </p>
          </div>
        )}

        {mode === "default" && (
          <p className={styles.hint} style={{ marginBottom: 14 }}>
            Clears both columns. This vendor then follows the platform default of{" "}
            {pct(data.platform_default_rate)}, which is an environment setting and
            can change without anyone touching this vendor.
          </p>
        )}

        {previewError && <p className={styles.err}>{previewError}</p>}
      </div>

      {/* ── the consequence, before saving ──────────────────────────── */}
      {changed && (
        <div className={styles.preview}>
          <div className={styles.previewHead}>
            <p className={styles.previewTitle}>
              What this does
              {proposedAgreement ? ` — ${describe(proposedAgreement)}` : ""}
            </p>
            {previewing && <span className={ops.secondary}>modelling…</span>}
          </div>

          {shown ? (
            <>
              <div className={styles.stats}>
                <div className={styles.stat}>
                  <span className={styles.statVal}>{shown.tiers_within}</span>
                  <span className={styles.statLbl}>
                    of {shown.live_tiers} live tiers already give this discount
                  </span>
                </div>
                <div className={styles.stat}>
                  <span
                    className={styles.statVal}
                    style={{ color: shown.tiers_over_ceiling ? "#a15c02" : undefined }}
                  >
                    {shown.tiers_over_ceiling}
                  </span>
                  <span className={styles.statLbl}>
                    would charge us more than it allows
                  </span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statVal}>{shown.tiers_without_list_price}</span>
                  <span className={styles.statLbl}>have no list price to check</span>
                </div>
              </div>

              {shown.sample && shown.sample.length > 0 && <TierTable tiers={shown.sample} />}

              <p className={ops.note}>
                No price or payout changes when you apply this. A tier over the
                new ceiling keeps selling as it is; the next time that vendor
                submits it, the price is refused until it gives at least this
                discount, and the approval screen flags it.
              </p>
            </>
          ) : (
            !previewing &&
            !previewError && (
              <p className={ops.empty}>Enter a value to see what it would do.</p>
            )
          )}

          <div className={ops.cardPad} style={{ paddingTop: 0 }}>
            <span className={styles.fieldLbl}>
              Why is this changing? (kept in the record below)
            </span>
            <textarea
              className={ops.textarea}
              value={note}
              placeholder="e.g. Renegotiated at the Q3 review — 22% agreed with Priya on 14 Aug, contract addendum #4."
              onChange={(e) => setNote(e.target.value)}
            />

            {zeroPayoutRisk && (
              <label className={styles.ack}>
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                />
                <span>
                  This amount is at or above the list price of{" "}
                  {shown.tiers_with_zero_ceiling} live tier(s), so the vendor
                  could charge us <b>₹0</b> at most on those. I understand and
                  this is intended.
                </span>
              </label>
            )}

            {saveError && <p className={styles.err}>{saveError}</p>}

            <div className={ops.actions}>
              <button
                className={ops.btnPrimary}
                disabled={saving || !proposal || (zeroPayoutRisk && !ack)}
                onClick={save}
              >
                {saving ? "Saving…" : "Apply agreement"}
              </button>
              <button className={ops.btn} disabled={saving} onClick={() => reset(data)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {saved && !changed && (
        <div className={`${ops.cardPad} ${ops.noteBox}`}>
          <b>Saved</b>
          <p>{saved}</p>
        </div>
      )}

      {/* ── history ─────────────────────────────────────────────────── */}
      <h3 style={{ borderTop: "1px solid #e4e9f0" }}>
        Change history
        <span className={ops.count}>{(data.history || []).length}</span>
      </h3>
      {data.history && data.history.length > 0 ? (
        <ul className={styles.hist}>
          {data.history.map((h) => (
            <li key={h.id} className={styles.histItem}>
              <i className={styles.histDot} />
              <div className={styles.histBody}>
                <span className={styles.histMove}>
                  {describe(h.before)} <span className={styles.arrow}>→</span>{" "}
                  {describe(h.after)}
                </span>
                <span className={styles.histMeta}>
                  {when(h.at)} · {h.by || "unknown admin"}
                  {h.impact && h.impact.tiers_that_would_move
                    ? ` · ${h.impact.tiers_that_would_move} tier(s) flagged at the time`
                    : ""}
                </span>
                {h.note && <span className={styles.histNote}>{h.note}</span>}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className={ops.empty}>
          No recorded change. This vendor has never had a margin agreed —
          the platform default has been carrying them.
        </p>
      )}
    </section>
  );
}
